import { useState, useEffect, useContext } from 'react';
import { Box, Typography, Chip, Stack } from '@mui/material';
import {
    Person as PersonIcon,
    AccessTime as DurationIcon,
    CardMembership as MemberIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttList,
    SttDialog,
    SttFormSelect,
} from '../../../sporttia/all';
import { formatPriceByLocale, fullName } from '../../../lib/utils';
import { Booking } from './BookingDialog';
import { DefaultListResponse200 } from '../../../types/api/utils';
import { PriceShape } from '../../../types/payment';
import constants from '../../../config/constants';
import { isDataphonePayment } from '../../paymentModal/refundDataphoneVisibility';

type OccupantRate = {
    id: number;
    name: string;
    price: number;
    duration?: number;
    priceShape?: PriceShape;
};

type OccupantUser = {
    id: number;
    name: string;
    fullName?: string;
};

type OccupantCreator = {
    id: number;
    fullName?: string;
    name?: string;
};

type OccupantMship = {
    id: number;
    fullName?: string;
    name?: string;
};

type Occupant = {
    id: number;
    user: OccupantUser;
    creator?: OccupantCreator;
    mship?: OccupantMship;
    rate?: OccupantRate[];
    impersonal?: boolean;
    paymentForm?: string;
};

type BookingDialogOccupantsProps = {
    booking: Booking;
    onUpdate?: (booking?: Booking) => void;
    onLastOccupantDeleted: () => void;
};

export default function BookingDialogOccupants({
    booking,
    onUpdate,
    onLastOccupantDeleted,
}: BookingDialogOccupantsProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [occupants, setOccupants] =
        useState<DefaultListResponse200<Occupant> | null>(null);

    // Entradas impersonales: ocupante pendiente de elegir forma de devolución
    // (efectivo/datáfono) al cancelar. Una entrada impersonal viene marcada
    // desde el backend; su devolución solo puede ser efectivo o datáfono.
    const [refundOccupant, setRefundOccupant] = useState<Occupant | null>(null);
    const [refundForm, setRefundForm] = useState<string | null>(null);

    const isImpersonalOccupant = (occupant: Occupant) =>
        occupant?.impersonal === true;

    /**
     * Load bono movs
     */
    const loadOccupants = () => {
        cxt?.api<DefaultListResponse200<Occupant>>(
            'GET',
            `/bookings/${booking.id}/occupants`,
            {
                params: { page: 1, rows: 800 },
                success: (r: any) => {
                    setOccupants(r);
                },
            },
        );
    };

    /**
     * Booking changes
     */
    useEffect(() => {
        if (booking && booking.id) {
            loadOccupants();
        }
    }, [booking]);

    /**
     * Format duration in minutes
     */
    const formatDuration = (minutes?: number): string => {
        if (!minutes) return '';
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
        }
        return `${minutes}min`;
    };

    /**
     * Render occupant card content
     */
    const renderOccupantContent = (occupant: Occupant) => {
        const userName = fullName(occupant.user) || occupant.user.name;
        const creatorName = occupant.creator
            ? fullName(occupant.creator) || occupant.creator.name
            : null;
        const mshipName = occupant.mship
            ? fullName(occupant.mship) || occupant.mship.name
            : null;

        return (
            <Box sx={{ width: '100%' }}>
                {/* User name - primary */}
                <Typography variant="subtitle1" fontWeight={600}>
                    {userName}
                </Typography>

                {/* Rates with price and duration */}
                {occupant.rate && occupant.rate.length > 0 && (
                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        sx={{ mt: 1, gap: 0.5 }}
                    >
                        {occupant.rate.map((rate) => (
                            <Chip
                                key={rate.id}
                                size="small"
                                label={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        <span>{rate.name}</span>
                                        <span>·</span>
                                        <strong>
                                            {formatPriceByLocale(
                                                rate.price,
                                                rate.priceShape?.currency ??
                                                    undefined,
                                                rate.priceShape?.locale,
                                            )}
                                        </strong>
                                        {rate.duration && (
                                            <>
                                                <DurationIcon
                                                    sx={{
                                                        fontSize: 14,
                                                        ml: 0.5,
                                                    }}
                                                />
                                                <span>
                                                    {formatDuration(
                                                        rate.duration,
                                                    )}
                                                </span>
                                            </>
                                        )}
                                    </Box>
                                }
                                sx={{
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                    '& .MuiChip-label': { px: 1 },
                                }}
                            />
                        ))}
                    </Stack>
                )}

                {/* Creator and Mship info */}
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ mt: 1 }}
                    flexWrap="wrap"
                >
                    {creatorName && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <PersonIcon
                                sx={{ fontSize: 16, color: 'text.secondary' }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {t('Creator')}: {creatorName}
                            </Typography>
                        </Box>
                    )}
                    {mshipName && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <MemberIcon
                                sx={{ fontSize: 16, color: 'text.secondary' }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {t('Member')}: {mshipName}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Box>
        );
    };

    /**
     * Actually delete the occupant. For impersonal entries `refundFormValue`
     * (CASH/DATAFONO) is sent and the confirmation is the refund dialog itself;
     * for nominal occupants we keep the plain confirmation and no refundForm
     * (the backend refunds to purse).
     */
    function performDelete(occupant: Occupant, refundFormValue?: string) {
        cxt?.api('DELETE', `/bookings/occupants/${occupant.id}`, {
            params: {
                cancelBookingOnLastOccupant: true, // TODO: modulo piscina cuando se puedan añadir entradas a la reserva (ver /src/pages/public/booked/Occupants.js)
                ...(refundFormValue ? { refundForm: refundFormValue } : {}),
            },
            success: () => {
                cxt?.showMessage('S', t('Deleted'));
                if (!occupants) return;

                const newRows = occupants.rows;
                newRows.splice(
                    newRows.findIndex((occ) => occ.id === occupant.id),
                    1,
                );
                setOccupants({ ...occupants, rows: newRows });

                if (newRows.length === 0) {
                    if (onLastOccupantDeleted) onLastOccupantDeleted();
                } else if (onUpdate) {
                    onUpdate();
                }
            },
            confirmation: refundFormValue === undefined,
        });
    }

    function deleteOccupant(occupant: Occupant) {
        if (!(occupant && occupant.id)) {
            return;
        }

        // Entrada impersonal: el cajero elige efectivo o datáfono (nunca
        // monedero). El diálogo de devolución hace de confirmación.
        if (isImpersonalOccupant(occupant)) {
            setRefundForm(null);
            setRefundOccupant(occupant);
            return;
        }

        performDelete(occupant);
    }

    // -----| Render |-----
    if (!occupants) return null;

    return (
        <>
            <SttList
                onDelete={deleteOccupant}
                data={occupants.rows.map((occupant: Occupant) => ({
                    caption: renderOccupantContent(occupant),
                    objToSelect: occupant,
                }))}
            />

            <SttDialog
                open={refundOccupant !== null}
                onClose={() => setRefundOccupant(null)}
                title={t('impersonalTicket.refundTitle')}
                content={
                    <Box>
                        <Typography variant="body1" mb={2}>
                            {t('impersonalTicket.refundWarning')}
                        </Typography>
                        <SttFormSelect
                            caption={t('SelectOneOption')}
                            defVal={refundForm}
                            onChange={({ value }: { value: unknown }) =>
                                setRefundForm(value as string)
                            }
                            options={[
                                {
                                    caption: t('impersonalTicket.refundCash'),
                                    value: constants.payment.paymentForms.cash
                                        .name,
                                },
                                // El datáfono solo se ofrece si el cobro
                                // original se hizo con datáfono; el efectivo
                                // siempre está disponible.
                                ...(isDataphonePayment(
                                    refundOccupant?.paymentForm,
                                )
                                    ? [
                                          {
                                              caption: t(
                                                  'impersonalTicket.refundDatafono',
                                              ),
                                              value: constants.payment
                                                  .paymentForms.dataphone.name,
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    </Box>
                }
                buttons={[
                    {
                        type: 'accept',
                        caption: t('Accept'),
                        disabled: refundForm === null,
                        onClick: () => {
                            if (refundOccupant && refundForm) {
                                performDelete(refundOccupant, refundForm);
                                setRefundOccupant(null);
                            }
                        },
                    },
                    {
                        type: 'close',
                        onClick: () => setRefundOccupant(null),
                    },
                ]}
            />
        </>
    );
}
